import React, { useState } from 'react';
import { apiService } from '../services/api';
import { useApi, useMutation } from '../hooks/useApi';
import logger from '../utils/logger';
import { useTheme } from '../context/ThemeContext';
import { UsersRound, Pencil, Trash2, PlusCircle } from 'lucide-react';
import Select from 'react-select';

function Perdoruesit() {
  const { theme } = useTheme();
  const [form, setForm] = useState({ emri: '', username: '', fjalekalimi: '', roli: 'shites', lokacion_id: '' });
  const [editId, setEditId] = useState(null);

  const { data: perdoruesitData, execute: refetchPerdoruesit } = useApi(
    () => apiService.get('/perdoruesit'),
    [],
    { immediate: true, showNotifications: false }
  );

  const { data: lokacionetData } = useApi(
    () => apiService.get('/lokacionet'),
    [],
    { immediate: true, showNotifications: false }
  );

  const perdoruesit = Array.isArray(perdoruesitData) ? perdoruesitData : (perdoruesitData?.data || []);
  const lokacionet = Array.isArray(lokacionetData) ? lokacionetData : (lokacionetData?.data || []);

  const { mutate: savePerdorues, loading: saving } = useMutation(
    async (data) => {
      if (editId) {
        return await apiService.put(`/perdoruesit/${editId}`, data);
      } else {
        return await apiService.post('/perdoruesit', data);
      }
    },
    {
      successMessage: editId ? 'Përdoruesi u përditësua me sukses!' : 'Përdoruesi u shtua me sukses!',
      onSuccess: () => {
        setForm({ emri: '', username: '', fjalekalimi: '', roli: 'shites', lokacion_id: '' });
        setEditId(null);
        refetchPerdoruesit();
      }
    }
  );

  const { mutate: deletePerdorues } = useMutation(
    async (id) => {
      return await apiService.delete(`/perdoruesit/${id}`);
    },
    {
      successMessage: 'Përdoruesi u fshi me sukses!',
      onSuccess: () => {
        refetchPerdoruesit();
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if ((form.roli === 'shites' || form.roli === 'menaxher') && !form.lokacion_id) {
      return;
    }

    savePerdorues(form);
  };

  const handleEdit = (user) => {
    setEditId(user.id);
    setForm({
      emri: user.emri,
      username: user.username,
      fjalekalimi: '',
      roli: user.roli,
      lokacion_id: user.lokacion_id || ''
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('A dëshironi të fshini këtë përdorues?')) return;
    deletePerdorues(id);
  };

  // Custom styles for react-select based on theme
  const selectStyles = theme === 'dark' ? {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#1e293b',
      borderColor: state.isFocused ? '#f59e0b' : '#475569',
      boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
      '&:hover': { borderColor: '#f59e0b' },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: '#1e293b',
      borderColor: '#475569',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#78350f' : state.isSelected ? '#f59e0b' : '#1e293b',
      color: state.isSelected ? 'white' : '#e2e8f0',
    }),
    singleValue: (base) => ({ ...base, color: '#e2e8f0' }),
    input: (base) => ({ ...base, color: '#e2e8f0' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8' }),
  } : {
    control: (base, state) => ({
      ...base,
      backgroundColor: 'transparent',
      borderColor: state.isFocused ? '#f59e0b' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
      '&:hover': { borderColor: '#f59e0b' },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'white',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#fef3c7' : state.isSelected ? '#fbbf24' : 'white',
      color: state.isSelected ? 'white' : '#111827',
    }),
  };

  return (
    <div className="w-full animate-fade-in">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gradient-logo flex items-center gap-3 animate-slide-down">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <UsersRound size={32} className="text-primary-600 dark:text-primary-400" />
        </div>
        Menaxhimi i Përdoruesve
      </h2>

      <form onSubmit={handleSubmit} className="card-premium p-6 mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <input type="text" placeholder="Emri" className="input-premium" value={form.emri} onChange={(e) => setForm({ ...form, emri: e.target.value })} required />
        <input type="text" placeholder="Përdoruesi" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="input-premium" required />
        <input type="password" placeholder="Fjalëkalimi" className="input-premium" value={form.fjalekalimi} onChange={(e) => setForm({ ...form, fjalekalimi: e.target.value })} required={!editId} />
        <select value={form.roli} onChange={(e) => setForm({ ...form, roli: e.target.value })} className="input-premium">
          <option value="shites">Shitës</option>
          <option value="menaxher">Menaxher</option>
          <option value="admin">Admin</option>
        </select>

        {(form.roli === 'shites' || form.roli === 'menaxher') && (
          <div className="col-span-full">
            <Select
              options={lokacionet.map(l => ({ value: l.id, label: l.emri }))}
              value={lokacionet.find(l => l.id === form.lokacion_id) ? {
                value: form.lokacion_id,
                label: lokacionet.find(l => l.id === form.lokacion_id)?.emri
              } : null}
              onChange={(option) => setForm({ ...form, lokacion_id: option?.value || '' })}
              placeholder="Zgjidh lokacionin"
              className="w-full"
              isClearable
              styles={selectStyles}
              classNamePrefix="react-select"
            />
          </div>
        )}

        <button type="submit" disabled={saving} className="col-span-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
          <PlusCircle size={20} />
          {saving ? 'Po ruhet...' : (editId ? 'Përditëso' : 'Shto Përdorues')}
        </button>
      </form>

      <div className="card-premium overflow-hidden">
        <table className="table-premium">
          <thead>
            <tr>
              <th>Emri</th>
              <th>Përdoruesi</th>
              <th>Roli</th>
              <th>Lokacioni</th>
              <th>Veprime</th>
            </tr>
          </thead>
          <tbody>
            {perdoruesit.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Nuk ka përdorues të regjistruar
                </td>
              </tr>
            ) : (
              perdoruesit.map((u) => (
                <tr key={u.id}>
                  <td className="font-medium">{u.emri}</td>
                  <td>{u.username}</td>
                  <td>
                    <span className={`badge-premium capitalize ${
                      u.roli === 'admin' ? 'badge-danger' : u.roli === 'menaxher' ? 'badge-warning' : 'badge-secondary'
                    }`}>
                      {u.roli}
                    </span>
                  </td>
                  <td>{u.lokacioni ? u.lokacioni.emri : '-'}</td>
                  <td>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => handleEdit(u)} className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-300 inline-flex items-center gap-1 transition-colors">
                        <Pencil size={16} /> Edito
                      </button>
                      <button onClick={() => handleDelete(u.id)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 inline-flex items-center gap-1 transition-colors">
                        <Trash2 size={16} /> Fshi
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Perdoruesit;
